import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { toast, Toaster } from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FiArrowLeft, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";
import { apiCall } from "utils/apiClient";
import {
  createMultipleServiceProcessSteps,
  deleteServiceProcessStep,
  deleteServiceProcessStepIcon,
  getServiceProcessStepsByService,
  sortServiceProcessSteps,
  updateMultipleServiceProcessSteps,
  uploadServiceProcessStepIcon,
} from "./serviceProcessApi";

const antIcon = <LoadingOutlined style={{ fontSize: 56 }} spin />;
const MEDIA_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";

const createEmptyStep = () => ({
  step_title: "",
  step_description: "",
  estimated_time: "",
  icon: "",
  status: "active",
});

const resolveMediaUrl = (path) => {
  if (!path) return "";

  const trimmed = String(path).trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const normalizedPath = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  const normalizedBase = MEDIA_BASE_URL.endsWith("/") ? MEDIA_BASE_URL : `${MEDIA_BASE_URL}/`;

  return `${normalizedBase}${normalizedPath}`;
};

const extractUploadedIconPath = (response) => {
  return response?.data?.icon || "";
};

const getIconLabel = (iconPath) => {
  const trimmed = String(iconPath || "").trim();
  if (!trimmed) return "";

  const segments = trimmed.split("/");
  return segments[segments.length - 1] || trimmed;
};

const AddServiceProcess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();

  const [service, setService] = useState(location.state?.service || null);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingSteps, setExistingSteps] = useState([]);
  const [processSteps, setProcessSteps] = useState([createEmptyStep()]);
  const [iconActionTarget, setIconActionTarget] = useState("");
  const [deletingStepTarget, setDeletingStepTarget] = useState("");

  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!serviceId) return;
      if (service?.id && String(service.id) === String(serviceId) && service?.name) return;

      setServiceLoading(true);

      try {
        const result = await apiCall({
          endpoint: `services/${serviceId}`,
          method: "GET",
        });

        if (result?.status === 200 && result?.data) {
          setService(result.data);
        } else {
          toast.error(result?.message || "Failed to fetch service details");
        }
      } catch (error) {
        console.error("Error fetching service details:", error);
        toast.error("Failed to fetch service details");
      } finally {
        setServiceLoading(false);
      }
    };

    fetchServiceDetails();
  }, [service?.id, service?.name, serviceId]);

  const loadProcessSteps = async () => {
    if (!serviceId) return;

    setStepsLoading(true);

    try {
      const result = await getServiceProcessStepsByService(serviceId);

      if (result?.status === 200) {
        setExistingSteps(Array.isArray(result?.data) ? sortServiceProcessSteps(result.data) : []);
        return;
      }

      if (result?.status === 404) {
        setExistingSteps([]);
        return;
      }

      setExistingSteps([]);
      toast.error(result?.message || "Failed to fetch service process steps");
    } catch (error) {
      console.error("Error fetching service process steps:", error);
      setExistingSteps([]);
      toast.error("Failed to fetch service process steps");
    } finally {
      setStepsLoading(false);
    }
  };

  useEffect(() => {
    loadProcessSteps();
  }, [serviceId]);

  const getStepTargetKey = (scope, index) => `${scope}-${index}`;

  const updateProcessStep = (index, field, value) => {
    setProcessSteps((prev) =>
      prev.map((step, stepIndex) =>
        stepIndex === index
          ? {
              ...step,
              [field]: value,
            }
          : step
      )
    );
  };

  const updateExistingStep = (index, field, value) => {
    setExistingSteps((prev) =>
      prev.map((step, stepIndex) =>
        stepIndex === index
          ? {
              ...step,
              [field]: value,
            }
          : step
      )
    );
  };

  const handleStepChange = (index, field, value) => {
    updateProcessStep(index, field, value);
  };

  const handleExistingStepChange = (index, field, value) => {
    updateExistingStep(index, field, value);
  };

  const handleAddStep = () => {
    setProcessSteps((prev) => [...prev, createEmptyStep()]);
  };

  const hasStepContent = (step) =>
    Boolean(
      step?.step_title?.trim() ||
        step?.step_description?.trim() ||
        step?.estimated_time?.trim() ||
        step?.icon?.trim()
    );

  const validateSteps = (steps, startIndex = 0) => {
    for (let index = 0; index < steps.length; index += 1) {
      const step = steps[index];
      const stepNumber = startIndex + index + 1;

      if (!step?.step_title?.trim()) {
        toast.error(`Step ${stepNumber} title is required`);
        return false;
      }

      if (!step?.step_description?.trim()) {
        toast.error(`Step ${stepNumber} description is required`);
        return false;
      }
    }

    return true;
  };

  const handleDeleteIcon = async (scope, index) => {
    const isExisting = scope === "existing";
    const steps = isExisting ? existingSteps : processSteps;
    const step = steps[index];
    const iconPath = step?.icon?.trim();

    if (!iconPath) {
      if (isExisting) {
        updateExistingStep(index, "icon", "");
      } else {
        updateProcessStep(index, "icon", "");
      }
      return true;
    }

    const targetKey = `${getStepTargetKey(scope, index)}-icon-delete`;
    setIconActionTarget(targetKey);

    try {
      const result = await deleteServiceProcessStepIcon(iconPath);

      if (result?.status === 200 || result?.status === 404) {
        if (isExisting) {
          updateExistingStep(index, "icon", "");
        } else {
          updateProcessStep(index, "icon", "");
        }
        toast.success("Icon removed successfully");
        return true;
      }

      toast.error(result?.message || "Failed to remove icon");
      return false;
    } catch (error) {
      console.error("Error deleting service process icon:", error);
      toast.error("Failed to remove icon");
      return false;
    } finally {
      setIconActionTarget("");
    }
  };

  const handleIconUpload = async (scope, index, file) => {
    if (!file) return;

    const isExisting = scope === "existing";
    const targetKey = `${getStepTargetKey(scope, index)}-icon-upload`;
    setIconActionTarget(targetKey);

    try {
      const result = await uploadServiceProcessStepIcon(file);
      const iconPath = extractUploadedIconPath(result);

      if ((result?.status === 200 || result?.status === 201) && iconPath) {
        if (isExisting) {
          updateExistingStep(index, "icon", iconPath);
        } else {
          updateProcessStep(index, "icon", iconPath);
        }
        toast.success("Icon uploaded successfully");
        return;
      }

      toast.error(result?.message || "Failed to upload icon");
    } catch (error) {
      console.error("Error uploading service process icon:", error);
      toast.error("Failed to upload icon");
    } finally {
      setIconActionTarget("");
    }
  };

  const handleRemoveNewStep = async (index) => {
    const step = processSteps[index];

    if (step?.icon?.trim()) {
      const deleted = await handleDeleteIcon("new", index);
      if (!deleted) return;
    }

    setProcessSteps((prev) => {
      if (prev.length === 1) return [createEmptyStep()];
      return prev.filter((_, stepIndex) => stepIndex !== index);
    });
  };

  const handleDeleteExistingStep = async (index) => {
    const step = existingSteps[index];
    const stepId = step?.id;

    if (!stepId) {
      toast.error("Step id is missing");
      return;
    }

    const targetKey = getStepTargetKey("existing-delete", index);
    setDeletingStepTarget(targetKey);

    try {
      const result = await deleteServiceProcessStep(stepId);

      if (result?.status === 200 || result?.status === 404) {
        setExistingSteps((prev) => prev.filter((_, stepIndex) => stepIndex !== index));
        toast.success("Service process step deleted successfully");
        return;
      }

      toast.error(result?.message || "Failed to delete service process step");
    } catch (error) {
      console.error("Error deleting service process step:", error);
      toast.error("Failed to delete service process step");
    } finally {
      setDeletingStepTarget("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!serviceId) {
      toast.error("Service id is missing");
      return;
    }

    const newStepsToCreate = processSteps.filter(hasStepContent);

    if (!validateSteps(existingSteps)) {
      return;
    }

    if (!validateSteps(newStepsToCreate, existingSteps.length)) {
      return;
    }

    if (existingSteps.length === 0 && newStepsToCreate.length === 0) {
      toast.error("Add at least one service process step");
      return;
    }

    setSubmitting(true);

    try {
      if (existingSteps.length > 0) {
        const updateResult = await updateMultipleServiceProcessSteps(serviceId, existingSteps);

        if (updateResult?.status !== 200 && updateResult?.status !== 201) {
          toast.error(updateResult?.message || "Failed to update service process steps");
          return;
        }
      }

      if (newStepsToCreate.length > 0) {
        const createResult = await createMultipleServiceProcessSteps(
          serviceId,
          newStepsToCreate,
          existingSteps.length
        );

        if (createResult?.status !== 200 && createResult?.status !== 201) {
          toast.error(createResult?.message || "Failed to add service process");
          return;
        }
      }

      toast.success("Service process steps saved successfully");
      setProcessSteps([createEmptyStep()]);
      await loadProcessSteps();
    } catch (error) {
      console.error("Error saving service process steps:", error);
      toast.error("Failed to save service process steps");
    } finally {
      setSubmitting(false);
    }
  };

  const renderIconField = (step, index, scope) => {
    const targetKey = getStepTargetKey(scope, index);
    const iconUrl = resolveMediaUrl(step?.icon);
    const iconLabel = getIconLabel(step?.icon);
    const isUploading = iconActionTarget === `${targetKey}-icon-upload`;
    const isDeletingIcon = iconActionTarget === `${targetKey}-icon-delete`;
    const isBusy = isUploading || isDeletingIcon;

    return (
      <div className="min-w-0 lg:col-span-2">
        <label className="mb-1.5 block text-xs font-medium text-slate-700">Icon</label>
        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 flex-1">
              {step?.icon ? (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                  {iconUrl ? (
                    <img
                      src={iconUrl}
                      alt="Service process icon"
                      className="h-11 w-11 shrink-0 rounded-lg border border-slate-200 object-cover"
                    />
                  ) : (
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Icon
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Uploaded Icon
                    </p>
                    <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">{iconLabel}</p>
                    <div className="mt-1 inline-flex max-w-full rounded-full bg-slate-100 px-2.5 py-1">
                      <p className="truncate font-mono text-[10px] text-slate-500">{step.icon}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-3 py-3 text-xs text-slate-500">
                  No icon uploaded yet.
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
              <label
                htmlFor={`${targetKey}-icon-file`}
                className={`inline-flex min-w-[112px] cursor-pointer items-center justify-center rounded-lg border border-indigo-300 px-3 py-2 text-xs font-medium text-indigo-700 ${
                  isBusy ? "pointer-events-none opacity-60" : "hover:bg-indigo-50"
                }`}
              >
                {isUploading ? "Uploading..." : step?.icon ? "Replace Icon" : "Upload Icon"}
              </label>
              <input
                id={`${targetKey}-icon-file`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  await handleIconUpload(scope, index, file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => handleDeleteIcon(scope, index)}
                disabled={isDeletingIcon || !step?.icon}
                className="inline-flex min-w-[112px] items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeletingIcon ? "Removing..." : "Remove Icon"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const displayServiceName = service?.name || service?.service_name || "-";
  const totalSteps = existingSteps.length + processSteps.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      {serviceLoading || stepsLoading ? (
        <div className="flex h-[75vh] w-full items-center justify-center">
          <Spin indicator={antIcon} />
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-[15px] font-semibold text-slate-800">Add Service Process</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Add multiple step-by-step process details for the selected service.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/service-processes")}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <FiArrowLeft size={14} />
                Back To Service Processes
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                  Service Name
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">{displayServiceName}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Total Steps
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">{totalSteps}</p>
              </div>
            </div>

            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              {existingSteps.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800">Saved Steps</h3>
                      <p className="mt-1 text-[11px] text-slate-500">
                        These rows are loaded from `service-process-steps/service/{serviceId}` and support update, delete, icon upload, and icon delete APIs.
                      </p>
                    </div>
                    <div className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 shadow-sm">
                      {existingSteps.length} saved
                    </div>
                  </div>

                  <div className="space-y-3">
                    {existingSteps.map((step, index) => {
                      const stepNumber = Number(step?.step_order) || index + 1;
                      const isDeletingStep =
                        deletingStepTarget === getStepTargetKey("existing-delete", index);

                      return (
                        <div
                          key={step?.id || `saved-step-${stepNumber}`}
                          className="rounded-lg border border-slate-200 bg-white px-4 py-3"
                        >
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[11px] font-semibold text-white">
                                {stepNumber}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-semibold text-slate-800">
                                  {step?.step_title || `Step ${stepNumber}`}
                                </h4>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteExistingStep(index)}
                              disabled={isDeletingStep}
                              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FiTrash2 size={12} />
                              {isDeletingStep ? "Deleting..." : "Delete Step"}
                            </button>
                          </div>

                          <div className="grid gap-3 lg:grid-cols-2">
                            <div className="min-w-0">
                              <label
                                className="mb-1.5 block text-xs font-medium text-slate-700"
                                htmlFor={`saved-step-title-${index}`}
                              >
                                Step Title
                              </label>
                              <input
                                id={`saved-step-title-${index}`}
                                type="text"
                                value={step.step_title || ""}
                                onChange={(e) =>
                                  handleExistingStepChange(index, "step_title", e.target.value)
                                }
                                placeholder="Enter step title"
                                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>

                            <div className="min-w-0">
                              <label
                                className="mb-1.5 block text-xs font-medium text-slate-700"
                                htmlFor={`saved-step-description-${index}`}
                              >
                                Step Description
                              </label>
                              <textarea
                                id={`saved-step-description-${index}`}
                                rows={3}
                                value={step.step_description || ""}
                                onChange={(e) =>
                                  handleExistingStepChange(index, "step_description", e.target.value)
                                }
                                placeholder="Enter step description"
                                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>

                            <div className="min-w-0">
                              <label
                                className="mb-1.5 block text-xs font-medium text-slate-700"
                                htmlFor={`saved-estimated-time-${index}`}
                              >
                                Estimated Time
                              </label>
                              <input
                                id={`saved-estimated-time-${index}`}
                                type="text"
                                value={step.estimated_time || ""}
                                onChange={(e) =>
                                  handleExistingStepChange(index, "estimated_time", e.target.value)
                                }
                                placeholder="Enter estimated time"
                                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>

                            <div className="min-w-0">
                              <label
                                className="mb-1.5 block text-xs font-medium text-slate-700"
                                htmlFor={`saved-status-${index}`}
                              >
                                Status
                              </label>
                              <select
                                id={`saved-status-${index}`}
                                value={step.status || "active"}
                                onChange={(e) => handleExistingStepChange(index, "status", e.target.value)}
                                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                              >
                                <option value="active">active</option>
                                <option value="inactive">inactive</option>
                              </select>
                            </div>

                            {renderIconField(step, index, "existing")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {processSteps.map((step, index) => (
                  <div
                    key={`step-${index + 1}`}
                    className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                  >
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-semibold text-white">
                          {existingSteps.length + index + 1}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-800">
                            Step {existingSteps.length + index + 1}
                          </h3>
                          <p className="text-[11px] text-slate-500">
                            Explain this service process in the correct order.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewStep(index)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-[11px] font-medium text-red-600 hover:bg-red-50"
                      >
                        <FiTrash2 size={12} />
                        Remove Step
                      </button>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      <div className="min-w-0">
                        <label
                          className="mb-1.5 block text-xs font-medium text-slate-700"
                          htmlFor={`step-title-${index}`}
                        >
                          Step Title
                        </label>
                        <input
                          id={`step-title-${index}`}
                          type="text"
                          value={step.step_title}
                          onChange={(e) => handleStepChange(index, "step_title", e.target.value)}
                          placeholder="Enter step title"
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div className="min-w-0">
                        <label
                          className="mb-1.5 block text-xs font-medium text-slate-700"
                          htmlFor={`step-description-${index}`}
                        >
                          Step Description
                        </label>
                        <textarea
                          id={`step-description-${index}`}
                          rows={3}
                          value={step.step_description}
                          onChange={(e) => handleStepChange(index, "step_description", e.target.value)}
                          placeholder="Enter step description"
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div className="min-w-0">
                        <label
                          className="mb-1.5 block text-xs font-medium text-slate-700"
                          htmlFor={`estimated-time-${index}`}
                        >
                          Estimated Time
                        </label>
                        <input
                          id={`estimated-time-${index}`}
                          type="text"
                          value={step.estimated_time}
                          onChange={(e) => handleStepChange(index, "estimated_time", e.target.value)}
                          placeholder="Enter estimated time"
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div className="min-w-0">
                        <label
                          className="mb-1.5 block text-xs font-medium text-slate-700"
                          htmlFor={`status-${index}`}
                        >
                          Status
                        </label>
                        <select
                          id={`status-${index}`}
                          value={step.status}
                          onChange={(e) => handleStepChange(index, "status", e.target.value)}
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                        </select>
                      </div>

                      {renderIconField(step, index, "new")}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={handleAddStep}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-300 px-4 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  <FiPlus size={14} />
                  Add Another Step
                </button>
                <button
                  type="submit"
                  disabled={submitting || Boolean(iconActionTarget) || Boolean(deletingStepTarget)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FiSave size={14} />
                  {submitting ? "Saving..." : "Save All Steps"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/service-processes")}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AddServiceProcess;
